import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Table,
  FormGroup,
  Label,
  Input,
  Spinner,
  Alert,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  useGetPurchaseOrderByIdQuery,
  useSendDeliveryRequisitionEmailMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

interface DeliveryEmailModalProps {
  isOpen: boolean;
  toggle: () => void;
  poId: number;
  poNumber: string;
  /** Optional: filter DR PDF by delivery date (YYYY-MM-DD) */
  deliveryDate?: string | null;
  /** Optional: row-level filter by PO item */
  itemId?: number | null;
  /** Optional: row-level filter by split delivery */
  splitId?: number | null;
  /** Optional: include defect items in DR PDF */
  isDefectItems?: boolean;
  /** Optional: this email is for a RE-DELIVERY (relabels the flow as Redelivery Requisition) */
  isRedelivery?: boolean;
}

const DeliveryEmailModal: React.FC<DeliveryEmailModalProps> = ({
  isOpen,
  toggle,
  poId,
  poNumber,
  deliveryDate: initialDeliveryDate,
  itemId = null,
  splitId = null,
  isDefectItems: initialIsDefectItems = false,
  isRedelivery = false,
}) => {
  const docLabel = isRedelivery ? "Redelivery Requisition" : "Delivery Requisition";
  const { data: poData, isLoading: loadingPO } = useGetPurchaseOrderByIdQuery(
    poNumber,
    { skip: !isOpen || !poNumber }
  );
  const [sendDeliveryRequisitionEmail, { isLoading: isSending }] =
    useSendDeliveryRequisitionEmailMutation();

  const [toEmails, setToEmails] = useState("");
  const [ccEmails, setCcEmails] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachPdf, setAttachPdf] = useState(true);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [deliveryDate, setDeliveryDate] = useState(initialDeliveryDate || "");
  const [isDefectItems, setIsDefectItems] = useState(initialIsDefectItems);

  // Whether the targeted delivery request (split or item) is cancelled — the
  // attached/downloadable PDF then carries the CANCELLED banner.
  const isTargetCancelled = React.useMemo(() => {
    const items = poData?.items || [];
    if (splitId) {
      for (const it of items) {
        const s = (it.split_deliveries || []).find(
          (sp: any) => Number(sp.id) === Number(splitId)
        );
        if (s) return s.delivery_status === "cancelled";
      }
      return false;
    }
    if (itemId) {
      const it = items.find((i: any) => Number(i.id) === Number(itemId));
      return it?.delivery_status === "cancelled";
    }
    return false;
  }, [poData, itemId, splitId]);

  const getSupplierEmailQty = (item: any) => {
    const ordered = parseFloat(String(item?.ordered_quantity ?? ""));
    if (!Number.isNaN(ordered) && ordered > 0) return ordered;
    return parseFloat(String(item?.quantity ?? 0)) || 0;
  };

  useEffect(() => {
    if (isOpen && poData) {
      setToEmails(poData.supplier_email || "");
      setSubject(`${docLabel} - ${poData.inventory_purchase_order_number}`);
      setBody(
        `<p>Please find attached the ${docLabel} for your reference.</p><p>Thank you for your business!</p>`
      );
    }
  }, [isOpen, poData, docLabel]);

  useEffect(() => {
    setDeliveryDate(initialDeliveryDate || "");
    setIsDefectItems(initialIsDefectItems ?? false);
  }, [initialDeliveryDate, initialIsDefectItems]);

  const filteredItems = (poData?.items || []).filter((row: any) => {
    if (!itemId && !splitId) return true;
    if (itemId && Number(row?.id) !== Number(itemId)) return false;
    if (splitId) {
      const splits = Array.isArray(row?.split_deliveries) ? row.split_deliveries : [];
      return splits.some((s: any) => Number(s?.id) === Number(splitId));
    }
    return true;
  });

  const getDisplayQtyAndDate = (item: any) => {
    if (!splitId) {
      // Redelivery preview: show the re-delivery quantity/date (matches the PDF)
      if (isRedelivery) {
        return {
          qty:
            parseFloat(String(item?.re_delivery_quantity ?? 0)) ||
            getSupplierEmailQty(item),
          expectedDate:
            item?.re_delivery_date || item?.po_expected_delivery_date || null,
        };
      }
      return {
        qty: getSupplierEmailQty(item),
        expectedDate: item?.po_expected_delivery_date || null,
      };
    }
    const splits = Array.isArray(item?.split_deliveries) ? item.split_deliveries : [];
    const targetSplit = splits.find((s: any) => Number(s?.id) === Number(splitId));
    if (!targetSplit) {
      return {
        qty: getSupplierEmailQty(item),
        expectedDate: item?.po_expected_delivery_date || null,
      };
    }
    // Redelivery preview for a split: use the split's re-delivery quantity/date
    if (isRedelivery) {
      return {
        qty:
          parseFloat(String(targetSplit?.re_delivery_quantity ?? 0)) ||
          parseFloat(String(targetSplit?.delivery_quantity ?? targetSplit?.quantity ?? 0)) ||
          0,
        expectedDate:
          targetSplit?.re_delivery_date ||
          targetSplit?.delivery_date ||
          item?.re_delivery_date ||
          item?.po_expected_delivery_date ||
          null,
      };
    }
    const splitQty = parseFloat(String(targetSplit?.delivery_quantity ?? targetSplit?.quantity ?? 0)) || 0;
    return {
      qty: splitQty,
      expectedDate:
        targetSplit?.delivery_date ||
        targetSplit?.po_expected_delivery_date ||
        item?.po_expected_delivery_date ||
        null,
    };
  };

  const getDisplayCost = (item: any) => {
    const baseCostRaw = item?.effective_total_item_cost ?? item?.total_item_cost;
    const baseCost = parseFloat(String(baseCostRaw ?? 0)) || 0;
    if (!splitId) return baseCost;

    const splits = Array.isArray(item?.split_deliveries) ? item.split_deliveries : [];
    const targetSplit = splits.find((s: any) => Number(s?.id) === Number(splitId));
    if (!targetSplit) return baseCost;

    const splitQty = parseFloat(
      String(targetSplit?.delivery_quantity ?? targetSplit?.quantity ?? 0)
    ) || 0;
    const supplierQty =
      parseFloat(
        String(
          item?.ordered_quantity ??
            item?.supplier_allocation_quantity ??
            item?.quantity ??
            0
        )
      ) || 0;

    if (supplierQty > 0 && splitQty >= 0) {
      return (baseCost * splitQty) / supplierQty;
    }
    return baseCost;
  };

  const generateDeliveryDetailsAndTableHTML = () => {
    if (!filteredItems.length) return "";

    const items = filteredItems as any[];
    const tableRows = items
      .map(
        (item: any, idx: number) => {
          const cost = getDisplayCost(item);
          const { qty, expectedDate } = getDisplayQtyAndDate(item);
          return `
        <tr>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">${idx + 1}</td>
          <td style="padding: 10px; border: 1px solid #dee2e6; background-color: #ffffff;">${item.product_name || "N/A"}</td>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">${qty.toFixed(2)}</td>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">${item.measurement_name || "-"}</td>
          <td style="padding: 10px; text-align: center; border: 1px solid #dee2e6; background-color: #ffffff;">${expectedDate ? new Date(expectedDate).toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" }) : "-"}</td>
          <td style="padding: 10px; text-align: right; border: 1px solid #dee2e6; background-color: #ffffff;">${cost.toFixed(2)}</td>
        </tr>`;
        }
      )
      .join("");

    return `
      <div style="font-family: Arial, sans-serif; max-width: 800px; margin: 0 auto; padding: 20px;">
        <div style="text-align: center; margin-bottom: 20px; border-bottom: 2px solid #198754; padding-bottom: 15px;">
          <h2 style="color: #198754; margin: 0; font-size: 24px;">DELIVERY REQUISITION DETAILS</h2>
        </div>
        <div style="margin-bottom: 20px;">
          <p style="margin: 5px 0;"><strong>PO Number:</strong> ${poData.inventory_purchase_order_number || "N/A"}</p>
          <p style="margin: 5px 0;"><strong>Supplier:</strong> ${poData.supplier_name || "N/A"}</p>
          <p style="margin: 5px 0;"><strong>PR Number:</strong> ${poData.purchase_request_number || "N/A"}</p>
          <p style="margin: 5px 0;"><strong>Date:</strong> ${new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })}</p>
          <p style="margin: 5px 0;"><strong>Total Items:</strong> ${items.length}</p>
        </div>
        <table style="width: 100%; border-collapse: collapse; border: 1px solid #dee2e6; font-family: Arial, sans-serif; font-size: 14px;">
          <thead>
            <tr style="background-color: #198754; color: #ffffff;">
              <th style="padding: 12px; text-align: center; border: 1px solid #198754; font-weight: bold;">No.</th>
              <th style="padding: 12px; text-align: left; border: 1px solid #198754; font-weight: bold;">Product</th>
              <th style="padding: 12px; text-align: center; border: 1px solid #198754; font-weight: bold;">Qty</th>
              <th style="padding: 12px; text-align: center; border: 1px solid #198754; font-weight: bold;">Unit</th>
              <th style="padding: 12px; text-align: center; border: 1px solid #198754; font-weight: bold;">Expected Date</th>
              <th style="padding: 12px; text-align: right; border: 1px solid #198754; font-weight: bold;">Total Cost</th>
            </tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </div>
      <div style="margin-top: 24px;"></div>`;
  };

  const handleSend = async () => {
    const toList = toEmails
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean);
    if (!toList.length) {
      toast.error("Please enter at least one recipient email");
      return;
    }
    if (!subject.trim()) {
      toast.error("Please enter email subject");
      return;
    }
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = body;
    const textContent = tempDiv.textContent || tempDiv.innerText || "";
    if (!textContent.trim()) {
      toast.error("Please enter email body");
      return;
    }

    try {
      const deliveryDetailsTableHTML = generateDeliveryDetailsAndTableHTML();
      const fullEmailBody = deliveryDetailsTableHTML + body;

      const result = await sendDeliveryRequisitionEmail({
        purchase_order_id: poId,
        to_emails: toList,
        cc_emails: ccEmails
          .split(",")
          .map((e) => e.trim())
          .filter(Boolean),
        subject,
        body: fullEmailBody,
        attach_pdf: attachPdf,
        is_defect_items: isDefectItems,
        delivery_date: deliveryDate || null,
        item_id: itemId || null,
        split_id: splitId || null,
        attachments,
      }).unwrap();
      toast.success(result?.message || "Email sent successfully");
      setAttachments([]);
      toggle();
    } catch (error: any) {
      const msg =
        error?.data?.error ||
        error?.data?.message ||
        error?.message ||
        "Failed to send email";
      toast.error(msg);
    }
  };

  const handleClose = () => {
    setToEmails("");
    setCcEmails("");
    setSubject("");
    setBody("");
    setAttachPdf(true);
    setAttachments([]);
    setDeliveryDate(initialDeliveryDate || "");
    setIsDefectItems(initialIsDefectItems ?? false);
    toggle();
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      size="xl"
      style={{ maxWidth: "95vw", width: "1200px" }}
    >
      <ModalHeader toggle={handleClose} className="bg-light py-3">
        <div className="d-flex align-items-center">
          <i className="ri-mail-send-line text-primary me-2 fs-5"></i>
          <div>
            <h5 className="modal-title mb-0 fw-bold">
              Send {docLabel} Email
            </h5>
            <small className="text-muted">
              {poNumber} - Compose and send {docLabel} to supplier
            </small>
          </div>
        </div>
      </ModalHeader>

      <ModalBody className="p-0">
        {loadingPO ? (
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-3">Loading purchase order...</p>
          </div>
        ) : !poData ? (
          <Alert color="danger">Failed to load purchase order</Alert>
        ) : (
          <div className="p-4">
            {/* Delivery Details - Read-only */}
            <div className="mb-4">
              <h6 className="mb-3 fw-semibold">
                <i className="ri-truck-line me-2"></i>
                {docLabel} Details
              </h6>
              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <small className="text-muted">PO Number</small>
                  <p className="mb-0 fw-medium">
                    {poData.inventory_purchase_order_number}
                  </p>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">Supplier</small>
                  <p className="mb-0 fw-medium">{poData.supplier_name}</p>
                </div>
                <div className="col-md-4">
                  <small className="text-muted">PR Number</small>
                  <p className="mb-0 fw-medium">
                    {poData.purchase_request_number}
                  </p>
                </div>
              </div>

              {/* Items Table - Read-only */}
              <div className="table-responsive">
                <Table bordered size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "40px" }}>#</th>
                      <th>Product</th>
                      <th className="text-center">Qty</th>
                      <th className="text-center">Unit</th>
                      <th className="text-center">Expected Date</th>
                      <th className="text-end">Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredItems.map((item: any, idx: number) => (
                      <tr key={`${item.id}-${splitId || "item"}`}>
                        <td className="text-center">{idx + 1}</td>
                        <td>{item.product_name || "N/A"}</td>
                        <td className="text-center">
                          {getDisplayQtyAndDate(item).qty.toFixed(2)}
                        </td>
                        <td className="text-center">
                          {item.measurement_name || "-"}
                        </td>
                        <td className="text-center">
                          {getDisplayQtyAndDate(item).expectedDate
                            ? new Date(
                                getDisplayQtyAndDate(item).expectedDate
                              ).toLocaleDateString()
                            : "-"}
                        </td>
                        <td className="text-end">
                          {getDisplayCost(item).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            {/* PDF Options */}
            <div className="mb-4">
              <h6 className="mb-3 fw-semibold">
                <i className="ri-file-pdf-line me-2"></i>
                PDF Options
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <FormGroup>
                    <Label for="deliveryDate">Filter by Delivery Date</Label>
                    <Input
                      id="deliveryDate"
                      type="date"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                    />
                    <small className="text-muted">
                      Optional: Leave empty for all items
                    </small>
                  </FormGroup>
                </div>
                <div className="col-md-6 d-flex align-items-end">
                  {isRedelivery ? (
                    <div className="text-info small">
                      <i className="ri-refresh-line align-middle me-1"></i>
                      Redelivery requisition — the PDF is generated for the
                      re-delivery quantities.
                    </div>
                  ) : (
                    <FormGroup check>
                      <Input
                        type="checkbox"
                        id="isDefectItems"
                        checked={isDefectItems}
                        onChange={(e) => setIsDefectItems(e.target.checked)}
                      />
                      <Label for="isDefectItems" check>
                        Include defect items in PDF
                      </Label>
                    </FormGroup>
                  )}
                </div>
              </div>
            </div>

            {/* Email Form - Editable */}
            <hr />
            <h6 className="mb-3 fw-semibold">
              <i className="ri-mail-line me-2"></i>
              Email
            </h6>

            <FormGroup>
              <Label for="toEmails">To (Supplier Email)</Label>
              <Input
                id="toEmails"
                type="text"
                value={toEmails}
                onChange={(e) => setToEmails(e.target.value)}
                placeholder="email@supplier.com"
              />
            </FormGroup>

            <FormGroup>
              <Label for="ccEmails">CC</Label>
              <Input
                id="ccEmails"
                type="text"
                value={ccEmails}
                onChange={(e) => setCcEmails(e.target.value)}
                placeholder="cc@example.com (comma-separated)"
              />
            </FormGroup>

            <FormGroup>
              <Label for="subject">Subject</Label>
              <Input
                id="subject"
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
              />
            </FormGroup>

            <FormGroup>
              <Label>Body</Label>
              <div className="border rounded overflow-hidden">
                <CKEditor
                  editor={ClassicEditor as any}
                  data={body}
                  onChange={(_: any, editor: any) => setBody(editor.getData())}
                  config={{
                    toolbar: [
                      "heading",
                      "|",
                      "bold",
                      "italic",
                      "link",
                      "bulletedList",
                      "numberedList",
                      "|",
                      "blockQuote",
                      "insertTable",
                      "|",
                      "undo",
                      "redo",
                    ],
                  }}
                />
              </div>
            </FormGroup>

            <FormGroup check className="d-flex align-items-center gap-3">
              <div>
                <Input
                  type="checkbox"
                  id="attachPdf"
                  checked={attachPdf}
                  onChange={(e) => setAttachPdf(e.target.checked)}
                />
                <Label for="attachPdf" check className="ms-1">
                  Attach {docLabel} PDF
                </Label>
              </div>
              {attachPdf && (
                <a
                  className="btn btn-sm btn-outline-primary"
                  target="_blank"
                  rel="noopener noreferrer"
                  href={`${window.__RUNTIME_CONFIG__.REACT_APP_API_URL}inventory/api/v1/delivery-requisition-pdf/${poNumber}?is_defect_items=${
                    isDefectItems ? "1" : "0"
                  }${deliveryDate ? `&delivery_date=${deliveryDate}` : ""}${
                    itemId ? `&item_id=${itemId}` : ""
                  }${splitId ? `&split_id=${splitId}` : ""}${
                    isTargetCancelled ? "&cancellation=1" : ""
                  }`}
                >
                  <i className="ri-download-2-line align-middle me-1"></i>
                  Download attached PDF
                </a>
              )}
            </FormGroup>

            <FormGroup className="mt-3">
              <Label className="fw-semibold">
                Additional Attachments (optional)
              </Label>
              <Input
                type="file"
                multiple
                onChange={(e) =>
                  setAttachments(Array.from(e.target.files || []))
                }
              />
              {attachments.length > 0 && (
                <ul className="mt-2 mb-0 ps-3 small">
                  {attachments.map((f, idx) => (
                    <li
                      key={idx}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <span className="text-truncate">{f.name}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0 ms-2"
                        onClick={() =>
                          setAttachments((prev) =>
                            prev.filter((_, i) => i !== idx)
                          )
                        }
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </FormGroup>
          </div>
        )}
      </ModalBody>

      {poData && (
        <ModalFooter>
          <Button color="light" onClick={handleClose}>
            Cancel
          </Button>
          <Button color="primary" onClick={handleSend} disabled={isSending}>
            {isSending ? (
              <>
                <Spinner size="sm" className="me-2" />
                Sending...
              </>
            ) : (
              <>
                <i className="ri-send-plane-fill me-1"></i>
                Send Email
              </>
            )}
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default DeliveryEmailModal;
